import React, { useState } from "react";
import { Dialog } from '@headlessui/react';
import { useTheme } from '../../CommonComponents/ThemeProvider';
import { PencilLine, X } from "lucide-react";

export default function EditStockModal({ isOpen, onClose, inventory, onSave, selectedBranchId = "" }) {
    const scopedQuantity = selectedBranchId
        ? (Number(inventory?.released_quantity) || 0)
        : (Number(inventory?.quantity) || 0);
    const stockLabel = selectedBranchId ? "Stocks Released" : "Stocks Available";
    const [quantity, setQuantity] = useState(scopedQuantity);
    const { currentTheme } = useTheme();

    React.useEffect(() => {
        if (isOpen && inventory) {
            setQuantity(scopedQuantity);
        }
    }, [isOpen, inventory, scopedQuantity]);

    if (!isOpen) return null;

    return (
        <Dialog
            open={isOpen}
            onClose={onClose}
            className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/45 px-4 backdrop-blur-sm"
        >
            <div className="w-full max-w-md">
                <Dialog.Panel className="relative overflow-hidden rounded-[28px] border border-white/70 bg-white p-6 shadow-[0_30px_80px_rgba(15,23,42,0.18)] md:p-7">
                    <div className="absolute inset-x-0 top-0 h-28 bg-[linear-gradient(135deg,rgba(15,23,42,0.05),rgba(148,163,184,0.04),transparent)]" />
                    <button
                        type="button"
                        onClick={onClose}
                        className="absolute right-4 top-4 z-10 inline-flex h-10 w-10 items-center justify-center rounded-full border border-slate-200 bg-white/90 text-slate-500 shadow-sm transition hover:text-slate-900"
                        aria-label="Close edit stock modal"
                    >
                        <X className="h-5 w-5" />
                    </button>

                    <div className="relative space-y-5">
                        <div className="inline-flex h-12 w-12 items-center justify-center rounded-2xl text-white shadow-sm" style={{ background: currentTheme.color }}>
                            <PencilLine className="h-5 w-5" />
                        </div>
                        <div>
                            <Dialog.Title className="text-xl font-semibold tracking-tight text-slate-900">
                                Edit Stock
                            </Dialog.Title>
                            <p className="mt-1 text-sm text-slate-500">
                                Update the selected inventory quantity with a clear, exact value.
                            </p>
                        </div>

                        <div className="rounded-2xl border border-slate-200 bg-slate-50/80 p-4 text-left">
                            <label className="block text-xs font-semibold uppercase tracking-[0.18em] text-slate-400 mb-2">{stockLabel}</label>
                            <input
                                type="number"
                                className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-lg font-semibold text-slate-900 shadow-sm transition focus:outline-none"
                                style={{ boxShadow: "0 8px 20px rgba(15,23,42,0.04)" }}
                                value={quantity}
                                min={0}
                                step={1}
                                inputMode="numeric"
                                onChange={e => setQuantity(Number(e.target.value))}
                                placeholder={`Enter new ${stockLabel.toLowerCase()} quantity`}
                            />
                        </div>

                        <div className="flex justify-end gap-3 pt-1">
                            <button
                                onClick={onClose}
                                className="rounded-2xl border border-slate-200 bg-slate-100 px-4 py-2.5 text-sm font-medium text-slate-700 transition hover:bg-slate-200 focus:outline-none"
                            >
                                Cancel
                            </button>
                            <button
                                onClick={() => onSave(quantity)}
                                className="rounded-2xl px-5 py-2.5 text-sm font-semibold text-white shadow-[0_14px_30px_rgba(15,23,42,0.16)] transition hover:opacity-90 focus:outline-none"
                                style={{ background: `linear-gradient(135deg, ${currentTheme.color}, color-mix(in srgb, ${currentTheme.color} 78%, black))` }}
                            >
                                Save Changes
                            </button>
                        </div>
                    </div>
                </Dialog.Panel>
            </div>
        </Dialog>
    );
}
